import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  clearError,
  createSchool,
  fetchSchools,
  selectSchools,
  selectSchoolsError,
  selectSchoolsLoading,
  selectSchoolsPage,
  selectSchoolsPageSize,
  selectSchoolsTotal,
  setPage,
  setPageSize,
  updateSchool,
} from "../../states/schools/SchoolsSlice";
import {
  CreateSchoolInput,
  SchoolData,
  UpdateSchoolInput,
} from "../../model/SchoolData";
import { useBase } from "../../context/BaseContext";
import EditSchoolModal from "../../components/EditSchoolModal";
import { EditIcon, EmptyBoxIcon } from "../../assets/svg";
import "../dashboard/Dashboard.css";

const SchoolsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const schools = useAppSelector(selectSchools);
  const isLoading = useAppSelector(selectSchoolsLoading);
  const error = useAppSelector(selectSchoolsError);
  const totalCount = useAppSelector(selectSchoolsTotal);
  const currentPage = useAppSelector(selectSchoolsPage);
  const pageSize = useAppSelector(selectSchoolsPageSize);
  const { showError, showSuccess } = useBase();

  const [modal, setModal] = useState<{
    isOpen: boolean;
    school: SchoolData | null;
  }>({ isOpen: false, school: null });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const reload = () =>
    dispatch(
      fetchSchools({
        page: currentPage,
        pageSize,
        searchKey: debouncedSearchTerm,
      }),
    );

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    if (error) {
      showError("Lỗi", error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const closeModal = () => setModal({ isOpen: false, school: null });

  const handleCreate = async (data: CreateSchoolInput) => {
    const result = await dispatch(createSchool(data));
    if (createSchool.fulfilled.match(result)) {
      showSuccess("Thành công", "Đã tạo trường học");
      closeModal();
      reload();
    }
  };

  const handleUpdate = async (data: UpdateSchoolInput) => {
    if (!modal.school) return;
    const result = await dispatch(
      updateSchool({ id: modal.school.id, data }),
    );
    if (updateSchool.fulfilled.match(result)) {
      showSuccess("Thành công", "Đã cập nhật trường học");
      closeModal();
    }
  };

  return (
    <>
      <EditSchoolModal
        isOpen={modal.isOpen}
        school={modal.school}
        onClose={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
      <div className="dashboard-container">
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card-content">
              <div className="dashboard-stat-flex">
                <div className="dashboard-stat-info">
                  <dl>
                    <dt className="dashboard-stat-label">Tổng số trường</dt>
                    <dd className="dashboard-stat-value">{totalCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setModal({ isOpen: true, school: null })}
            sx={{
              textTransform: "none",
              height: "fit-content",
              alignSelf: "center",
            }}
          >
            + Thêm trường học
          </Button>
        </div>

        <div className="dashboard-table-container">
          <div className="dashboard-table-header">
            <input
              type="text"
              placeholder="Tìm theo tên trường..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                dispatch(setPage(1));
              }}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
                outline: "none",
              }}
            />
          </div>

          {isLoading ? (
            <div className="dashboard-loading">
              <div className="dashboard-loading-content">
                <div className="dashboard-loading-spinner" />
                <p className="dashboard-loading-text">Đang tải trường học...</p>
              </div>
            </div>
          ) : schools.length === 0 ? (
            <div className="dashboard-empty">
              <EmptyBoxIcon className="dashboard-empty-icon" />
              <h3 className="dashboard-empty-title">
                {searchTerm ? "Không tìm thấy trường" : "Chưa có trường nào"}
              </h3>
              <p className="dashboard-empty-description">
                {searchTerm
                  ? `Không có trường nào khớp "${searchTerm}"`
                  : 'Bấm "Thêm trường học" để bắt đầu.'}
              </p>
            </div>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
              <Table aria-label="schools table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: "bold" }}>Tên trường</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Địa chỉ</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>
                      Số điện thoại
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Ngày tạo</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }} align="center">
                      Hành động
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schools.map((s) => (
                    <TableRow
                      key={s.id}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 500 }}>{s.name}</Typography>
                      </TableCell>
                      <TableCell>{s.address || ""}</TableCell>
                      <TableCell>{s.phoneNumber || ""}</TableCell>
                      <TableCell>{s.email || ""}</TableCell>
                      <TableCell>
                        {new Date(s.createdAt).toLocaleDateString("vi-VN")}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => setModal({ isOpen: true, school: s })}
                          color="primary"
                          size="small"
                          title="Sửa"
                        >
                          <EditIcon style={{ width: 20, height: 20 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={currentPage - 1}
                onPageChange={(_, newPage) => dispatch(setPage(newPage + 1))}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  dispatch(setPageSize(parseInt(event.target.value, 10)));
                  dispatch(setPage(1));
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default SchoolsPage;
